import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../api";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { ArrowLeft, Home } from "lucide-react";
import { getOrCreateSessionId } from "../utils/session";

export default function PreOrderPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ If HomePage passed product via navigate, use it as initial state
  const initialProduct = location.state?.product || null;
  const [product, setProduct] = useState(initialProduct);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [sliderRef] = useKeenSlider({ loop: false });

  // --- Fetch product if not provided by HomePage ---
  useEffect(() => {
    if (product) return; // already have from HomePage, skip fetch

    const fetchProduct = async () => {
      try {
        const sessionId = getOrCreateSessionId();
        const res = await api.get(`/cproduct/${productId}`, {
          params: { session_id: sessionId },
        });
        setProduct(res.data);
      } catch (err) {
        console.error("Failed to fetch product:", err);
        setError("Product not found or failed to load.");
      }
    };
    fetchProduct();
  }, [productId, product]);

  // --- Defaults from customization_schema ---
  const defaultColor =
    product?.customization_schema?.find(f => f.label === "Color")?.options?.[0]?.label || "";
  const defaultMaterial =
    product?.customization_schema?.find(f => f.label === "Material")?.options?.[0]?.label || "";
  const defaultSize =
    product?.customization_schema?.find(f => f.label === "Size")?.options?.[0]?.label || "";

  // --- Helper to get images for a color (handles both object and list-of-objects) ---
  const getImagesForColor = (imageMap, color) => {
    if (!imageMap || !color) return [];
    if (Array.isArray(imageMap)) {
      // list-of-objects format
      const entry = imageMap.find(e => e.color.toLowerCase() === color.toLowerCase());
      return entry?.urls || [];
    }
    if (typeof imageMap === "object") {
      // object format
      return imageMap[color.toLowerCase()] || [];
    }
    return [];
  };

  // --- Default image ---
  const getDefaultColorImage = () => {
    const images = getImagesForColor(product?.imageUrl, defaultColor);
    return images[0] || "";
  };

  // --- Flatten all images for slider ---
  const allImages = useMemo(() => {
    if (!product) return [];

    const images = [];
    const defaultImg = getDefaultColorImage();
    if (defaultImg) images.push(defaultImg);

    if (product.imageUrl) {
      if (Array.isArray(product.imageUrl)) {
        // list-of-objects
        product.imageUrl.forEach(entry => {
          entry.urls.forEach(url => {
            if (url && url !== defaultImg) images.push(url);
          });
        });
      } else {
        // object
        Object.values(product.imageUrl).forEach(urls => {
          if (Array.isArray(urls)) {
            urls.forEach(url => {
              if (url && url !== defaultImg) images.push(url);
            });
          } else if (typeof urls === "string" && urls !== defaultImg) {
            images.push(urls);
          }
        });
      }
    }

    return images;
  }, [product, defaultColor]);

  // --- Price & total ---
  let unitPrice = 0;
  if (Array.isArray(product?.price) && product.price.length > 0) {
    unitPrice = Number(product.price[0]) || 0;
  } else if (typeof product?.price === "number") {
    unitPrice = product.price;
  }
  const totalPrice = useMemo(() => unitPrice * quantity, [unitPrice, quantity]);

  const handleCheckout = () => {
    navigate(`/checkout/${productId}`, { state: { product, quantity } });
  };
  const handleCustomize = () => {
    navigate(`/customize/${productId}`, { state: { product, quantity } });
  };

  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!product) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="relative flex items-center mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-black"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
        </button>
        <button
          onClick={() => navigate("/")}
          className="absolute left-1/2 transform -translate-x-1/2 text-gray-600 hover:text-black"
        >
          <Home className="w-5 h-5" />
        </button>
      </div>

      {/* Image Slider */}
      <div ref={sliderRef} className="keen-slider mb-6 rounded-lg overflow-hidden">
        {allImages.map((url, idx) => (
          <div key={idx} className="keen-slider__slide">
            <div className="w-full aspect-[2/3] md:min-h-[350px] overflow-hidden rounded-lg">
              <img
                src={url}
                alt={`Product image ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Description */}
      <p className="text-gray-700 mb-2">{product.description}</p>

      {/* Default Parameters Table */}
      <div className="mb-4 border rounded-lg overflow-hidden">
        <table className="w-full text-left border-collapse">
          <tbody>
            {defaultColor && (
              <tr className="border-b">
                <td className="bg-gray-100 px-4 py-2 font-semibold">Color</td>
                <td className="px-4 py-2">{defaultColor}</td>
              </tr>
            )}
            {defaultMaterial && (
              <tr className="border-b">
                <td className="bg-gray-100 px-4 py-2 font-semibold">Material</td>
                <td className="px-4 py-2">{defaultMaterial}</td>
              </tr>
            )}
            {defaultSize && (
              <tr>
                <td className="bg-gray-100 px-4 py-2 font-semibold">Size</td>
                <td className="px-4 py-2">{defaultSize}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Price & Quantity */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-semibold">Unit Price: ₹{unitPrice}</p>
          <p className="text-gray-600 text-sm">Total: ₹{totalPrice}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            className="px-3 py-1 bg-gray-300 rounded-full"
            onClick={() => setQuantity(q => Math.max(1, q - 1))}
          >
            -
          </button>
          <span className="min-w-[2rem] text-center">{quantity}</span>
          <button
            className="px-3 py-1 bg-gray-300 rounded-full"
            onClick={() => setQuantity(q => q + 1)}
          >
            +
          </button>
        </div>
      </div>

      {/* Buttons */}
      <button
        onClick={handleCustomize}
        className="mb-3 w-full px-6 py-2 border border-black text-black bg-white rounded-full shadow-sm hover:bg-gray-100 transition"
      >
        Customize
      </button>

      <button
        onClick={handleCheckout}
        className="w-full px-6 py-2 bg-black text-white rounded-full"
      >
        Proceed to Checkout
      </button>
    </div>
  );
}
